/**
 * Pure request-building helpers for `useChat`: payload shape, auth/thread
 * headers, attachment encoding and the non-streaming POST. Nothing in here
 * touches React state; `useChat` owns the send pipeline that calls these.
 */
import type { Message, RetryPolicy, SessionConfig } from '../types';
import { fetchWithRetry } from '../utils/retryFetch';
import { messageAnalyticsContext } from '../utils/messageAnalyticsContext';
import { threadCapabilityHeaders } from '../utils/threadCapability';

export interface AttachmentPayload {
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
}

/** Attachment as held by the composer (before encoding for the wire). */
export interface ComposerAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  file?: File;
  previewUrl?: string | null;
}

export interface ChatPayloadArgs {
  text: string;
  attachments?: AttachmentPayload[];
  subject?: string;
  tid?: string;
  meta?: Record<string, unknown>;
}

export interface ChatPayloadContext {
  subject: string;
  department: string;
  channel: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  provider?: string;
  threadId: string | null;
  sessionConfig?: SessionConfig;
  /** Current transcript; the last 30 entries ride along as `threadMessages`. */
  messages: Message[];
}

interface RetryMetadata {
  attempt: number;
  reason: 'status' | 'error';
  status?: number;
  error?: Error;
}

/** Shared request body for the streaming and JSON paths. */
export function buildChatPayload(
  { text, attachments: atts = [], subject: subj, tid, meta }: ChatPayloadArgs,
  ctx: ChatPayloadContext
) {
  const analyticsContext = messageAnalyticsContext(ctx.sessionConfig);
  return {
    subject: subj || ctx.subject || (text ? text.slice(0, 80) : 'New message'),
    message: text,
    attachments: (atts || []).map(a => ({
      name: a.name,
      type: a.type,
      size: a.size,
      ...(a.dataUrl ? { dataUrl: a.dataUrl } : {}),
    })),
    meta: {
      ...analyticsContext,
      department: ctx.department,
      customFields: ctx.customFields,
      ...(ctx.metaExtras || {}),
      ...(meta || {}),
    },
    sessionId: analyticsContext.sessionId,
    source: 'response-chat-widget',
    ...(ctx.provider ? { provider: ctx.provider } : {}),
    channel: ctx.channel,
    threadId: tid || ctx.threadId,
    threadMessages: ctx.messages.slice(-30).map(msg => ({
      id: msg.id,
      sender: msg.sender,
      text: msg.text,
      timestamp: msg.timestamp,
      type: msg.type,
      status: msg.status,
      richContent: msg.richContent,
      attachments: msg.attachments?.map(attachment => ({
        name: attachment.name,
        type: attachment.type,
        size: attachment.size,
      })),
    })),
  };
}

/**
 * W1 (docs/contracts/chat-widget.md): the chat POST must carry the per-thread
 * capability like the thread routes already do, or the platform's
 * WIDGET_THREAD_CAP_ENFORCE flip 403s every chat. `sessionConfig` selects the
 * storage the token was written to (`storage: 'session'` -> sessionStorage).
 */
export function buildChatHeaders(
  withAuthHeaders: (headers?: Record<string, string>) => Record<string, string>,
  threadId: string | null | undefined,
  extra?: Record<string, string>,
  sessionConfig?: SessionConfig
): Record<string, string> {
  return {
    ...withAuthHeaders(),
    ...threadCapabilityHeaders(threadId, sessionConfig),
    ...(extra || {}),
  };
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  });
}

/** Encodes composer attachments for the wire; unreadable files fall back to metadata only. */
export async function buildAttachmentPayload(
  attachments: Pick<ComposerAttachment, 'name' | 'type' | 'size' | 'file'>[]
): Promise<AttachmentPayload[]> {
  if (!attachments || attachments.length === 0) return [];
  return Promise.all(
    attachments.map(async att => {
      const base = { name: att.name, type: att.type, size: att.size };
      if (att.file && typeof FileReader !== 'undefined') {
        try {
          const dataUrl = await readFileAsDataUrl(att.file);
          return { ...base, dataUrl };
        } catch {
          return base;
        }
      }
      return base;
    })
  );
}

/** Server-provided detail from a failed response body (`error`, then `message`). */
export async function readErrorDetail(res: Response): Promise<string> {
  try {
    const json = (await res.json()) as Record<string, unknown>;
    if (typeof json?.error === 'string') return json.error;
    if (typeof json?.message === 'string') return json.message;
  } catch {
    // Keep generic status code message if body is not JSON.
  }
  return '';
}

/** Non-streaming chat POST; resolves to the parsed JSON body or throws with the HTTP status. */
export async function postChatJson({
  endpoint,
  body,
  headers,
  signal,
  retryPolicy,
  onRetry,
}: {
  endpoint: string;
  body: unknown;
  headers: Record<string, string>;
  signal?: AbortSignal;
  retryPolicy?: RetryPolicy;
  onRetry?: (metadata: RetryMetadata) => void;
}): Promise<Record<string, unknown>> {
  const res = await fetchWithRetry(
    endpoint,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal,
      timeoutMs: 30_000,
    },
    retryPolicy,
    { onRetry }
  );
  if (!res.ok) {
    const serverError = await readErrorDetail(res);
    throw new Error(
      `Message create failed: ${res.status}${serverError ? ` (${serverError})` : ''}`
    );
  }
  return (await res.json()) as Record<string, unknown>;
}
